/* eslint-disable no-unused-vars */
/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, omit, pathOr, prop, propEq, toLower } from 'ramda';
import isEmail from 'validator/lib/isEmail';
import { useMachine } from '@xstate/react';
import styled from 'styled-components';

import { Box, Stack } from '../../components/ui';
import loginEnhancer from '../../containers/LoginEnhancer';
import {
  LocalStorage,
  retrieveAllUriParams,
  retrieveUriParam,
  updateUserClaims
} from '../../../../utils/helpers';
import General from '../../layouts/general';
import { LoginMachine } from './machines';
import {
  logUserIn,
  sendAuthLink,
  signUserIn,
  verifyAccessToken,
  resetPassword
} from '../../../../core/auth/util';
import Input from '../../components/Input';
import Button from '../../components/Button';
import AccountCard from '../../components/AccountCard';
import {
  ButtonWrapper,
  Centered,
  ForgotPasswordLink,
  InputWrapper,
  LinksWrapper,
  Notification,
  Row,
  UseAnotherAccountLink,
  Or,
  LegalText,
  LegalLink
} from './components';
import Main from '../../layouts/main';
import { theme } from '../../../../utils/styles';

const Content = styled(Stack)`
  flex: 1;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const AccountCards = styled(Stack)`
  font-size: 0.9rem;
  margin: 1em 2em;
  width: 100%;
  align-items: center;
`;

const Description = ({ children, ...props }) => <Box {...props}>{children}</Box>;
const Descriptions = styled(Stack)`
  width: 90%;
  margin: 1em auto;
  font-size: 1.1rem;
  text-align: center;
  font-family: ${theme.barlow};
  color: ${theme.textGray};
`;

export const Login = ({ users, usersInfo, validatePassword, removeAccount, client, message }) => {
  const formRef = useRef();
  const appName = useMemo(() => retrieveUriParam('app_name'), []);
  const params = retrieveAllUriParams();
  const [state, send] = useMachine(LoginMachine, {
    services: {
      authenticate: async (ctx, { data }) => {
        const username = data.email;
        const response = await signUserIn({
          username,
          password: data.password,
          new_password: data.new_password || '',
          firstName: data.firstName,
          lastName: data.lastName,
          isNew: data.isNew || false,
          params: omit(['client_id', 'redirect_uri'], params)
        });
        if (response.userTokens && !response.emailVerification) {
          const localUsers = LocalStorage.getArray('usernames');
          if (!localUsers.includes(username.toLowerCase())) {
            localUsers.push(username.toLowerCase());
            LocalStorage.setObject('usernames', localUsers);
          }

          LocalStorage.set('user.username', username.toLowerCase());
          if (propEq('newPasswordRequired', false)(response)) {
            logUserIn({ username, userTokens: prop('userTokens', response) });
          }
        }
        return response;
      },
      authenticateLink: async (ctx, { data }) => {
        let response;
        if (data.reset) {
          response = await resetPassword({
            username: data.email
          });
        } else if (data.link) {
          response = await sendAuthLink({
            email: data.email,
            clientId: params.client_id,
            redirectUri: params.redirect_uri,
            params: omit(['client_id', 'redirect_uri', 'error'], params)
          });
        }
        return response;
      },
      authenticateToken: async (ctx, { data }) => {
        const { email } = data;
        const idToken = LocalStorage.get(`${email}.userTokens.idToken`);
        const accessToken = LocalStorage.get(`${email}.userTokens.accessToken`);
        const refreshToken = LocalStorage.get(`${email}.userTokens.refreshToken`);
        const valid = await verifyAccessToken(accessToken);
        if (!valid) {
          throw new Error('Invalid Access Token');
        }
        updateUserClaims({
          email,
          clientId: params.client_id
        });
        return {
          userTokens: {
            idToken,
            accessToken,
            refreshToken
          }
        };
      }
    },
    context: {
      params
    }
  });
  useEffect(
    () => {
      if (users && users.length !== 0) {
        send('HAS_USERS');
      }
    },
    [users]
  );
  const userTokens = pathOr({}, ['context', 'userTokens'], state);
  const description = pathOr('', ['context', 'description'], state);
  const [email, setEmail] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState();
  useEffect(
    () => {
      const error = pathOr(false, ['context', 'error'], state);
      if (error) {
        setErrorMessage(error);
      }
    },
    [state.context.error]
  );
  const [passwordConfirm, setPasswordConfirm] = useState();
  const onContinue = useCallback(
    async () => {
      setErrorMessage('');
      if (state.matches('email')) {
        // Send Check email request
        if (isEmail(email)) {
          send('VERIFY_EMAIL', { email });
        } else {
          setErrorMessage('Please enter a valid email address');
        }
      } else if (state.matches('password') || state.matches('password.error')) {
        // Send authentication request
        send('AUTHENTICATE', { data: { email, password } });
      } else if (state.matches('forgotPassword.init')) {
        send('AUTHENTICATE_LINK', { data: { email, link: true } });
      } else if (state.matches('forgotPassword.sent')) {
        send('RESET');
      } else if (state.matches('newPassword')) {
        const results = validatePassword({ password, passwordConfirm });
        if (isEmpty(results)) {
          send('AUTHENTICATE', {
            data: { email, password: state.context.password, new_password: password }
          });
        } else {
          setErrorMessage(results.password || results.passwordConfirm);
        }
      } else if (state.matches('newUser')) {
        const results = validatePassword({ password, passwordConfirm });
        if (isEmpty(results)) {
          send('AUTHENTICATE', {
            data: { email, password, firstName, lastName, isNew: true }
          });
        } else {
          setErrorMessage(results.password || results.passwordConfirm);
        }
      }
    },
    [state, email, password, passwordConfirm, firstName, lastName]
  );
  const onSendReset = useCallback(
    async () => {
      setErrorMessage('');
      send('SEND_RESET_LINK', { data: { email, reset: true } });
    },
    [state, email]
  );
  const isDisabled = pathOr(false, ['context', 'buttonDisabled'], state);
  const buttonLabel = pathOr(false, ['context', 'buttonLabel'], state);
  const redirectUri = useMemo(
    () =>
      `${params.redirect_uri}?${new URLSearchParams(
        omit(['redirect_uri', 'client_id', 'error', 'outputType'], params)
      ).toString()}`,
    [params]
  );
  useEffect(
    // eslint-disable-next-line consistent-return
    () => {
      if (state.matches('redirect')) {
        const timeout = setTimeout(() => {
          if (toLower(params.outputType || '') === 'get') {
            const tokenParams = new URLSearchParams({
              accessToken: userTokens.accessToken,
              idToken: userTokens.idToken,
              refreshToken: userTokens.refreshToken
            });
            window.location.href = `${redirectUri}&${tokenParams}`;
            return;
          }
          if (formRef.current) {
            formRef.current.submit();
          }
        }, 1000);
        return () => {
          clearTimeout(timeout);
        };
      }
    },
    [state, formRef.current, params, redirectUri, userTokens]
  );

  if (message && message.status === 'InvalidURL') {
    return <Main error>{message.text}</Main>;
  }

  return (
    <Main client={client}>
      <General title={appName} description={description}>
        <Content>
          {['newUser', 'password', 'email', 'forgotPassword', 'newPassword'].some(
            state.matches
          ) && (
            <Box style={{ maxWidth: '22em', width: '100%' }}>
              <InputWrapper>
                {['email', 'forgotPassword.init', 'forgotPassword.sending'].some(state.matches) && (
                  <Input
                    type="text"
                    name="email"
                    required
                    value={email}
                    placeholder="Email Address"
                    onChange={e => {
                      const { value } = e.target;
                      setEmail(value);
                      setErrorMessage('');
                    }}
                  />
                )}
                {state.matches('password') &&
                  !state.matches('password.authenticating') && (
                    <form onSubmit={onContinue}>
                      <Input
                        type="password"
                        name="password"
                        required
                        label="password"
                        value={password}
                        placeholder="Password"
                        onChange={e => {
                          // clearErrors();
                          if (e.target.value !== password) {
                            setErrorMessage('');
                          }
                          setPassword(e.target.value);
                        }}
                      />
                    </form>
                  )}
                {state.matches('newPassword') && (
                  <Fragment>
                    <Row>
                      <Input
                        type="password"
                        name="password"
                        required
                        value={password}
                        placeholder="Password"
                        onChange={e => {
                          setPassword(e.target.value);
                        }}
                      />
                    </Row>
                    <Row>
                      <Input
                        type="password"
                        name="passwordConfirm"
                        required
                        value={passwordConfirm}
                        placeholder="Confirm Password"
                        onChange={e => {
                          setPasswordConfirm(e.target.value);
                        }}
                      />
                    </Row>
                  </Fragment>
                )}
                {state.matches('newUser') && (
                  <Fragment>
                    {/* <Row>
                      <Input
                      type="text"
                      name="firstName"
                      value={firstName}
                      placeholder="First Name"
                      onChange={e => {
                        setFirstName(e.target.value);
                        }}
                        />
                        </Row>
                        <Row>
                        <Input
                        type="text"
                        name="lastName"
                        value={lastName}
                        placeholder="Last Name"
                        onChange={e => {
                          setLastName(e.target.value);
                          }}
                          />
                          </Row> */}
                    <Row>
                      <Input
                        type="password"
                        name="password"
                        required
                        value={password}
                        placeholder="Password"
                        onChange={e => {
                          setPassword(e.target.value);
                        }}
                      />
                    </Row>
                    <Row>
                      <Input
                        type="password"
                        name="passwordConfirm"
                        required
                        value={passwordConfirm}
                        placeholder="Confirm Password"
                        onChange={e => {
                          setPasswordConfirm(e.target.value);
                        }}
                      />
                    </Row>
                  </Fragment>
                )}
              </InputWrapper>
            </Box>
          )}
          {params.error &&
            params.error === 'link-invalid' && (
              <Notification>
                <div>The link you used was either invalid or expired.</div>
                <div>Please try again.</div>
              </Notification>
            )}
          {!state.matches('redirect') &&
            errorMessage && <Notification>{errorMessage}</Notification>}
          {['newUser', 'password', 'email', 'forgotPassword', 'newPassword'].some(
            state.matches
          ) && (
            <Box style={{ maxWidth: '22em', width: '100%' }}>
              <ButtonWrapper>
                <Button
                  name="continue"
                  label={buttonLabel}
                  onClick={onContinue}
                  primary
                  disabled={isDisabled}
                />
              </ButtonWrapper>
            </Box>
          )}
          {['newPassword', 'newUser'].some(state.matches) && (
            <LegalText>
              By choosing &apos;Continue&apos;, you agree to the Stack Sports{' '}
              <LegalLink href="https://stacksports.com/legal-privacy" target="_blank">
                Privacy Policy
              </LegalLink>{' '}
              and{' '}
              <LegalLink href="https://stacksports.com/legal-terms" target="_blank">
                Terms of Service
              </LegalLink>
            </LegalText>
          )}
          {state.matches('forgotPassword.init') &&
            !state.matches('forgotPassword.sent') && (
              <Box style={{ maxWidth: '22em', width: '100%' }}>
                <Or />
                <ButtonWrapper>
                  <Button
                    name="continue"
                    label="Reset Password"
                    onClick={onSendReset}
                    primary
                    disabled={isDisabled}
                  />
                </ButtonWrapper>
              </Box>
            )}
          {['password.init', 'password.error'].some(state.matches) && (
            <Fragment>
              <LinksWrapper>
                {state.matches('password') && (
                  <ForgotPasswordLink onClick={() => send('FORGOT_PASSWORD')}>
                    Forgot your password?
                  </ForgotPasswordLink>
                )}
              </LinksWrapper>
              <LegalText>
                By choosing &apos;Continue&apos;, you agree to the Stack Sports{' '}
                <LegalLink href="https://stacksports.com/legal-privacy" target="_blank">
                  Privacy Policy
                </LegalLink>{' '}
                and{' '}
                <LegalLink href="https://stacksports.com/legal-terms" target="_blank">
                  Terms of Service
                </LegalLink>
              </LegalText>
            </Fragment>
          )}

          {state.matches('multiple') && (
            <Fragment>
              <AccountCards spacing={3}>
                {users.map(user => {
                  const userInfo = usersInfo[user];
                  const hasTokens = !!LocalStorage.get(`${toLower(user)}.userTokens.accessToken`);
                  const userFirstName = userInfo && userInfo.firstName;
                  return (
                    <AccountCard
                      key={user}
                      onClick={() => {
                        setEmail(user);
                        send('AUTHENTICATE_TOKEN', { data: { email: user } });
                      }}
                      name={userFirstName && `${userFirstName} ${userInfo.lastName}`}
                      firstName={userFirstName}
                      email={user}
                      hasTokens={hasTokens}
                      removeAccount={removeAccount}
                    />
                  );
                })}
              </AccountCards>
              <Centered>
                <LinksWrapper>
                  <UseAnotherAccountLink
                    onClick={() => {
                      send('USE_ANOTHER');
                    }}
                  >
                    Use another account?
                  </UseAnotherAccountLink>
                </LinksWrapper>
              </Centered>
            </Fragment>
          )}
          {state.matches('verification') && (
            <Descriptions spacing={3}>
              <Description>
                <span>Thanks! To continue, please verify your email address</span>
                <span style={{ fontWeight: 'bold' }}> {email} </span>
                <span>by clicking the link in the email we just sent you.</span>
              </Description>
              <Description>
                We&#39;ve enhanced our account creation process in order to minimize duplicate
                accounts and need to verify your email to continue.
              </Description>
              <Description>
                The email may take a few minutes to show up in your inbox. If you don&#39;t receive
                the email in a few minutes, please check your SPAM folder.
              </Description>
            </Descriptions>
          )}
          {state.matches('redirect') && (
            <form method="POST" action={redirectUri} ref={formRef}>
              <input type="hidden" name="idToken" value={userTokens.idToken} />
              <input type="hidden" name="accessToken" value={userTokens.accessToken} />
              <input type="hidden" name="refreshToken" value={userTokens.refreshToken} />
              <input type="hidden" name="redirectUri" value={params.redirectUri} />
            </form>
          )}
        </Content>
      </General>
    </Main>
  );
};

export default loginEnhancer(Login);



// WEBPACK FOOTER //
// ./src/views/sports-connect/pages/Login/index.js